<template>
<div class="odr-upload">
    <div style="display: none !important;">
        <div id="dwtcontrolContainer"></div>
        <style-unsafe v-if="scanInProgress">
            .dynamsoft-backdrop, .dynamsoft-dialog-wrap, .dynamsoft-dwt-dialogProgress  { display: none !important; }
        </style-unsafe>
    </div>

    <drag-file v-if="!input && enableSelect"
               ref="dragFile"
               :invisible="removeImagePlaceholder"
               :width="ratioX * maxPreviewWidth"
               :height="ratioY * maxPreviewWidth"
               :lastpass="lastpass"
               :icon="'collections'"
               :accept="'image/*'"
               @focus="$emit('focus')"
               @blur="$emit('blur')"
               @changed="data => {rawInput = data.result}"/>

    <button v-if="!input && !enableSelect && !removeImagePlaceholder"
            type="button"
            class="odr-upload__dummy"
            @click="clickOnDummy"
            :style="`width: ${ratioX * maxPreviewWidth}px !important; \
                     height: ${ratioY * maxPreviewWidth}px !important;`">
        <div class="odr-upload__dummy-icon">
            <icon size="l" color="default">{{ dummyIcon }}</icon>
        </div>
    </button>

    <img v-if="placeholder && !input && !taking"
         class="odr-upload__image"
         :style="`width: ${ratioX * maxPreviewWidth}px !important;`"
         :src="placeholder">

    <img v-if="input && !taking"
         class="odr-upload__image"
         :class="{'odr-upload__image_progress': scanInProgress || fileUpload_inProgress || fileUpload_failed}"
         :style="`width: ${ratioX * maxPreviewWidth}px !important;`"
         :src="input">

    <div :style="`width: ${ratioX * maxPreviewWidth}px !important;`">
        <linearProgress v-if="scanInProgress || fileUpload_inProgress"/>
    </div>

    <text-regular v-if="fileUpload_failed" color="error">{{ $trl('aw.upload_image_invalid') }}</text-regular><br>
    <text-link v-if="fileUpload_failed"
               bold="true"
               color="error"
               @click="retry">
        {{ $trl('aw.upload_image_retry') }}
    </text-link>

    <dropdown v-if="taking"
              :fullscreen="true"
              :keep-alive="true"
              :value="true"
              :on-close="stopCamera">
        <div slot="content" class="odr-upload__dropdown">
            <div class="odr-upload__video">
                <video ref="sourceVideo"
                       autoplay
                       webkit-playsinline="true"
                       playsinline="true"
                       style="display: none !important;"></video>
                <video ref="video"
                       class="odr-upload__stream"
                       autoplay
                       webkit-playsinline="true"
                       playsinline="true"/>
                <div v-if="gettingVideo" class="odr-upload__mask">
                    <div class="odr-upload__mask-rectangle"
                         :style="`width: ${ratioX * maskRatioPercentage}% !important; \
                                  height: ${ratioY * maskRatioPercentage * ratioVideo}% !important;`"/>
                    <button type="button"
                            class="odr-upload__mask-button"
                            @click="takePhoto">
                        <icon>photo_camera</icon>
                        {{ $trl('aw.take_photo') }}
                    </button>
                </div>
            </div>
        </div>
    </dropdown>

    <gap top="single" left="negative-single" right="negative-single">
        <gap v-if="hasGetUserMedia && !input && enableCamera"
             left="single"
             right="single">
            <main-button v-if="enableCamera && !enableScan && !enableSelect"
                         type="button"
                         @focus="() => {$emit('focus')}"
                         @blur="() => {$emit('blur')}"
                         @click="openCamera">
                {{ $trl('aw.open_camera') }}
            </main-button>
            <text-link bold="true"
                       v-else
                       @focus="() => {$emit('focus')}"
                       @blur="() => {$emit('blur')}"
                       @click="openCamera">
                {{ $trl('aw.open_camera') }}
            </text-link>
        </gap>

        <gap v-if="!input && enableSelect"
             left="single"
             right="single">
            <main-button v-if="!enableCamera && !enableScan && enableSelect"
                         ref="choose"
                         type="button"
                         @focus="() => {$emit('focus')}"
                         @blur="() => {$emit('blur')}"
                         @click="selectFile">
                {{ $trl('aw.choose') }}
            </main-button>
            <text-link bold="true"
                       v-else
                       @focus="() => {$emit('focus')}"
                       @blur="() => {$emit('blur')}"
                       @click="selectFile">
                {{ $trl('aw.choose') }}
            </text-link>
        </gap>

        <gap v-if="!input && eEnabledScan"
             left="single"
             right="single">
            <main-button v-if="!enableCamera && enableScan && !enableSelect"
                         ref="choose"
                         type="button"
                         @focus="() => {$emit('focus')}"
                         @blur="() => {$emit('blur')}"
                         @click="scan">
                {{ $trl('aw.upload_image_scan') }}
            </main-button>
            <text-link bold="true"
                       v-else
                       @focus="() => {$emit('focus')}"
                       @blur="() => {$emit('blur')}"
                       @click="scan">
                {{ $trl('aw.upload_image_scan') }}
            </text-link>
        </gap>

        <gap v-if="input"
             left="single"
             right="single">
            <text-link bold="true"
                       @focus="() => {$emit('focus')}"
                       @blur="() => {$emit('blur')}"
                       @click="clear">
                {{ $trl('aw.remove') }}
            </text-link>
        </gap>
    </gap>
    <div v-if="validation_showError && validation_error"
         class="odr-error-message"
         :class="{
             'odr-error-message_plain': $root.appConfig.mode !== 'chat'
         }">
        {{ validation_error }}
    </div>
</div>
</template>

<script>
import '@/scss/blocks/button-link.scss';
import '@/scss/blocks/error-message.scss';
import '@/scss/blocks/upload.scss';

import { dataURItoBlob } from '@/utils/blob';
import validationMixin from '@/utils/mixins/validation';
import fileUploadMixin from '@/utils/mixins/file-upload';
import focusMixin from '@/utils/mixins/focus';
import { showNotifier } from '@/utils/notifier';
import dropdown from '@/vue/components/dropdown';
import insertScript from '@/utils/insert-script';
import { safeParse } from '@/utils/json';

if (navigator.mediaDevices === undefined && (navigator.webkitGetUserMedia || navigator.mozGetUserMedia)) {
    navigator.mediaDevices = {
        getUserMedia: function (constraints) {
            return new Promise(function (resolve, reject) {
                (navigator.webkitGetUserMedia || navigator.mozGetUserMedia).call(navigator, constraints, resolve, reject);
            });
        },
    };
}

export default {
    mixins: [validationMixin, fileUploadMixin, focusMixin],
    props: {
        stateId: {},
        dataQa: {},
        value: {},
        title: {},
        placeholder: {},
        ratio: {
            default: '16:9',
        },
        maskRatioPercentage: {
            default: 90,
        },
        maxPreviewWidth: {
            default: 240,
        },
        lastpass: {
            type: Boolean,
        },
        sizeLimit: {
            default: 2000,
        },
        enableScan: {
            default: false,
        },
        enableSelect: {
            default: true,
        },
        enableCamera: {
            default: true,
        },
        scanSettings: {},
        resizeImage: {
            default: false,
        },
        removeImagePlaceholder: {
            default: false,
        },
    },
    mounted () {
        if (this.enableScan) {
            if (!window.Dynamsoft) {
                window.Dynamsoft = window.Dynamsoft || { DWT: {} };
                window.Dynamsoft.DWT.AutoLoad = true;
                window.Dynamsoft.DWT.Containers = [{ ContainerId: 'dwtcontrolContainer', Width: 270, Height: 350 }];
                window.Dynamsoft.DWT.ProductKey = this.$root.appConfig.dynamsoftLicenceKey;
                window.Dynamsoft.DWT.Trial = false;
                window.Dynamsoft.DWT.ResourcesPath = 'https://cdn.odoro.com/dynamsoft/17.3/';
                window.Dynamsoft.DWT.IfAddMD5InUploadHeader = false;
                window.Dynamsoft.DWT.IfConfineMaskWithinTheViewer = false;
                insertScript({ src: window.Dynamsoft.DWT.ResourcesPath + '/dynamsoft.webtwain.initiate.js' }).then(() => {
                    if (window.Dynamsoft) {
                        this.scanResourceReady = true;
                    }
                });
            } else {
                this.scanResourceReady = true;
            }
        }

        if (this.enableCamera) {
            window.addEventListener('resize', this.recalcRatioVideo);
        }
    },
    data () {
        return {
            input: this.value instanceof Object ? this.value.base64 : this.value,
            taking: false,
            gettingVideo: false,
            scanResourceReady: false,
            scanInProgress: false,
            stream: null,
            rawInput: null,
            ratioVideo: 0,
        };
    },
    computed: {
        eEnabledScan () {
            return this.enableScan && this.scanResourceReady;
        },
        eScanSettings () {
            if (this.scanSettings) {
                return safeParse(this.scanSettings);
            }
            return {};
        },
        eInput () {
            if (this.uploadToken) {
                return this.fileUpload_succeed ? { token: this.uploadToken, base64: this.input } : '';
            } else {
                return { base64: this.input };
            }
        },
        ratioParsed () {
            return this.ratio.split(':').map(value => parseInt(value, 10));
        },
        ratioX () {
            let [x, y] = this.ratioParsed;
            return (Math.max(x, y) === x ? 1 : x / y);
        },
        ratioY () {
            let [x, y] = this.ratioParsed;
            return (Math.max(x, y) === x ? y / x : 1);
        },
        hasGetUserMedia () {
            return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        },
        dummyIcon () {
            if (this.enableCamera) {
                return 'camera_enhance';
            } else if (this.enableScan && this.scanResourceReady) {
                return 'scanner';
            }

            return 'collections';
        },
    },
    watch: {
        rawInput () {
            this.resize(this.rawInput).then(input => {
                this.input = input;
            });
        },
        input () {
            this.validation_changed();

            if (this.uploadToken && this.input) {
                this.fileUpload_send(dataURItoBlob(this.input));
            }
        },
        eInput () {
            this.$emit('input', this.eInput);
        },
    },
    methods: {
        recalcRatioVideo () {
            if (this.gettingVideo && this.$refs.video) {
                console.log('rec');
                this.ratioVideo = this.$refs.video.videoWidth / this.$refs.video.videoHeight;
            }
        },
        clickOnDummy () {
            if (this.enableCamera) {
                this.openCamera();
            } else if (this.enableScan && this.scanResourceReady) {
                this.scan();
            }
        },
        scan () {
            let DWObject = window.Dynamsoft.DWT.GetWebTwain('dwtcontrolContainer');
            if (DWObject) {
                this.scanInProgress = true;
                DWObject.startScan({
                    ui: {
                        bShowUI: false,
                        bShowIndicator: false,
                    },
                    settings: {
                        bFeeder: true,
                        bDuplex: true,
                        pixelType: window.Dynamsoft.DWT.EnumDWT_PixelType.TWPT_RGB,
                        resolution: 300,
                        ...this.eScanSettings,
                    },
                }).then(() => {
                    return Promise.all(Array(DWObject.HowManyImagesInBuffer).fill(true).map((value, index) => {
                        return new Promise((resolve, reject) => {
                            DWObject.ConvertToBase64([index], window.Dynamsoft.DWT.EnumDWT_ImageType.IT_PNG, base64 => {
                                resolve('data:image/png;base64,' + base64.getData(0, base64.getLength()));
                            }, reject);
                        });
                    })).then(base64images => {
                        if (base64images.length > 1) {
                            return Promise.all(base64images.map(base64 => new Promise((resolve) => {
                                let image = new Image();
                                image.onload = function () {
                                    resolve(image);
                                };
                                image.src = base64;
                            }))).then((images) => {
                                let width = Math.max.apply(Math, images.map(image => image.width));
                                let canvas = document.createElement('canvas');

                                canvas.width = width;
                                canvas.height = images.map(image => image.height).reduce((a, b) => a + b, 0);

                                let shiftHeight = 0;
                                images.forEach(image => {
                                    canvas.getContext('2d').drawImage(image, 0, shiftHeight, image.width, image.height);
                                    shiftHeight += image.height;
                                });

                                this.input = canvas.toDataURL();
                            });
                        } else {
                            this.input = base64images[0];
                        }
                    });
                }).finally(() => {
                    DWObject.RemoveAllImages();
                    this.scanInProgress = false;
                });
            }
        },
        resize (rawInput) {
            return new Promise((resolve) => {
                if (!rawInput || !this.resizeImage) {
                    resolve(rawInput);
                }

                let image = new Image();
                image.onload = () => {
                    if (image.width > this.sizeLimit || image.height > this.sizeLimit) {
                        let maxSize = Math.max(image.width, image.height);
                        let rate = maxSize / this.sizeLimit;

                        let canvas = document.createElement('canvas');
                        let width = image.width / rate;
                        let height = image.height / rate;
                        canvas.width = width;
                        canvas.height = height;
                        canvas.getContext('2d').drawImage(image, 0, 0, width, height);
                        resolve(canvas.toDataURL());
                    } else {
                        resolve(rawInput);
                    }
                };
                image.src = rawInput;
            });
        },
        retry () {
            this.fileUpload_send(dataURItoBlob(this.input));
        },
        focus_focus () {
            if (this.$refs.dragFile && this.$refs.dragFile.$refs.file) {
                this.$refs.dragFile.$refs.file.focus({ preventScroll: true });
            }
        },
        validation_isEmpty () {
            return !this.eInput;
        },
        stopCamera () {
            if (this.stream) {
                this.stream.getTracks().forEach(track => track.stop());
                this.gettingVideo = false;
                this.taking = false;
            }
        },
        clear () {
            if (this.xhr) {
                this.xhr.abort();
            }
            this.input = null;
        },
        selectFile () {
            this.$refs.dragFile.$refs.file.click();
        },
        takePhoto () {
            let canvas = document.createElement('canvas');
            let multiplyX = this.ratioX * this.maskRatioPercentage / 100;
            let multiplyY = this.ratioY * this.maskRatioPercentage * this.ratioVideo / 100;

            canvas.width = this.$refs.sourceVideo.videoWidth * multiplyX;
            canvas.height = this.$refs.sourceVideo.videoHeight * multiplyY;

            canvas.getContext('2d').drawImage(
                this.$refs.sourceVideo,
                -1 * (1 - multiplyX) * this.$refs.sourceVideo.videoWidth / 2,
                -1 * (1 - multiplyY) * this.$refs.sourceVideo.videoHeight / 2,
                this.$refs.sourceVideo.videoWidth,
                this.$refs.sourceVideo.videoHeight,
            );

            this.taking = false;
            this.input = canvas.toDataURL();
            this.stopCamera();
        },
        openCamera () {
            this.taking = true;
            this.$nextTick(() => {
                function getCamera () {
                    let resolutions = [
                        {
                            width: 1920,
                            height: 1080,
                        },
                        {
                            width: 1600,
                            height: 900,
                        },
                        {
                            width: 1366,
                            height: 768,
                        },
                        {
                            width: 1280,
                            height: 720,
                        },
                        {
                            width: 960,
                            height: 540,
                        },
                        {
                            width: 640,
                            height: 360,
                        },
                    ];
                    let resolutionIndex = 0;

                    function iterateConstraints () {
                        function tryResolution ({ width, height }) {
                            console.log(width, height);
                            return navigator.mediaDevices.getUserMedia({
                                audio: false,
                                video: {
                                    facingMode: 'environment',
                                    width: { exact: width },
                                    height: { exact: height },
                                },
                            });
                        }
                        return new Promise((resolve, reject) => {
                            tryResolution(resolutions[resolutionIndex]).then(resolve).catch(error => {
                                resolutionIndex++;
                                if (resolutions[resolutionIndex]) {
                                    resolve(iterateConstraints(resolutionIndex));
                                } else {
                                    reject(error);
                                }
                            });
                        });
                    }

                    return iterateConstraints();
                }

                getCamera().then(localMediaStream => {
                    this.$refs.video.srcObject = localMediaStream;
                    this.$refs.sourceVideo.srcObject = localMediaStream;
                    this.stream = localMediaStream;

                    this.$refs.video.onloadedmetadata = () => {
                        this.gettingVideo = true;
                        this.ratioVideo = this.$refs.video.videoWidth / this.$refs.video.videoHeight;
                    };
                }).catch((error) => {
                    console.error(error);
                    showNotifier({
                        stateId: this.stateId,
                        title: this.$trl('aw.taking_photo_blocked'),
                        autoHide: true,
                    });

                    this.taking = false;
                });
            });
        },
        validation_invalid () {
            return this.fileUpload_inProgress;
        },
    },
    components: {
        dropdown,
        linearProgress: require('@/vue/markup/linear-progress.vue').default,
        'drag-file': require('@/vue/components/drag-file.vue').default,
        icon: require('@clearwave/odoro-markup/lib/vue/icon.vue').default,
        gap: require('@clearwave/odoro-markup/lib/vue/gap.vue').default,
        'text-link': require('@clearwave/odoro-markup/lib/vue/text-link.vue').default,
        'text-regular': require('@clearwave/odoro-markup/lib/vue/text-regular.vue').default,
        'style-unsafe': require('@clearwave/odoro-markup/lib/vue/style-unsafe.vue').default,
        'main-button': require('@clearwave/odoro-markup/lib/vue/main-button.vue').default,

    },
};
</script>
